import { createRef, useCallback, useRef } from 'react';

type DraftTab = { id: string; name: string };

type UseTabDragReorderingParams = {
  draftTabs: DraftTab[];
  reorderTabs: (from: number, to: number) => void;
  addTab: (tabId: string, tabName: string) => void;
};

export const useTabDragReordering = ({
  draftTabs,
  reorderTabs,
  addTab
}: UseTabDragReorderingParams) => {
  const tabHandleRefsMap = useRef(new Map<string, React.RefObject<HTMLDivElement | null>>());

  const getTabHandleRef = useCallback((tabId: string): React.RefObject<HTMLDivElement | null> => {
    let ref = tabHandleRefsMap.current.get(tabId);
    if (!ref) {
      ref = createRef<HTMLDivElement>();
      tabHandleRefsMap.current.set(tabId, ref);
    }
    return ref;
  }, []);

  const handleTabDragEnd = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dnd-kit event shape is library-defined and not exported here.
    (event: any) => {
      if (event.canceled) return;

      const { source, target } = event.operation;
      if (source?.type !== 'dashboard-tab') return;

      let fromIndex: number;
      let toIndex: number;

      if (target?.type === 'dashboard-tab' && target.id !== source.id) {
        fromIndex = draftTabs.findIndex(t => t.id === source.id);
        toIndex = draftTabs.findIndex(t => t.id === target.id);
      } else if (typeof source.index === 'number' && typeof source.initialIndex === 'number') {
        fromIndex = source.initialIndex;
        toIndex = source.index;
      } else {
        return;
      }

      if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
        reorderTabs(fromIndex, toIndex);
      }
    },
    [draftTabs, reorderTabs]
  );

  const handleTabCreated = useCallback(
    (tabName: string, tabId: string) => {
      addTab(tabId, tabName);
    },
    [addTab]
  );

  return { getTabHandleRef, handleTabDragEnd, handleTabCreated };
};
